import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Database,
  Download,
  Loader2,
  Plus,
  Search,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { type AdminTenant, getAdminTenants, PlatformApiError } from '@/lib/api'
import {
  filterTenants,
  type SortDir,
  type SortKey,
  sortTenants,
  tenantsToCsv,
} from '@/lib/tenant-list'
import { cn } from '@/lib/utils'

const STATUSES = ['active', 'paused', 'pending_deletion', 'suspended'] as const
const TIERS = ['personal', 'business', 'enterprise'] as const
const SORT_KEYS: readonly SortKey[] = ['name', 'tier', 'status', 'members', 'modules', 'createdAt']

export interface ListSearch {
  q?: string
  status?: string
  tier?: string
  sort?: SortKey
  dir?: SortDir
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined
}

export const Route = createFileRoute('/platform/tenants/')({
  validateSearch: (search: Record<string, unknown>): ListSearch => ({
    q: typeof search.q === 'string' && search.q ? search.q : undefined,
    status: oneOf(search.status, STATUSES),
    tier: oneOf(search.tier, TIERS),
    sort: oneOf(search.sort, SORT_KEYS),
    dir: oneOf(search.dir, ['asc', 'desc'] as const),
  }),
  component: PlatformTenantsPage,
})

function PlatformTenantsPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'loaded'; tenants: AdminTenant[] }
    | { status: 'error'; message: string }
  >({ status: 'loading' })

  useEffect(() => {
    // No didRun guard here — in React StrictMode the effect mounts
    // twice. The abort pattern handles that cleanly: cleanup aborts
    // the first fetch (catch returns early on AbortError), the
    // second mount starts a fresh fetch that completes. A
    // didRun-ref + abort combo would leave us stuck in loading
    // because the guarded second run would skip the new fetch.
    const ctrl = new AbortController()
    getAdminTenants(ctrl.signal)
      .then((tenants) => setState({ status: 'loaded', tenants }))
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        const message =
          err instanceof PlatformApiError && err.message ? err.message : 'Failed to load tenants'
        setState({ status: 'error', message })
      })
    return () => ctrl.abort()
  }, [])

  const sort = search.sort ?? 'createdAt'
  const dir = search.dir ?? (sort === 'createdAt' ? 'desc' : 'asc')

  const visible = useMemo(() => {
    if (state.status !== 'loaded') return []
    const filtered = filterTenants(state.tenants, {
      q: search.q ?? '',
      status: search.status ?? '',
      tier: search.tier ?? '',
    })
    return sortTenants(filtered, sort, dir)
  }, [state, search.q, search.status, search.tier, sort, dir])

  const setSearch = (patch: Partial<ListSearch>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  const toggleSort = (key: SortKey) => {
    if (key === sort) setSearch({ sort: key, dir: dir === 'asc' ? 'desc' : 'asc' })
    else setSearch({ sort: key, dir: key === 'createdAt' ? 'desc' : 'asc' })
  }

  const exportCsv = () => {
    const blob = new Blob([tenantsToCsv(visible)], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'tenants.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const total = state.status === 'loaded' ? state.tenants.length : 0
  const filtering = Boolean(search.q || search.status || search.tier)

  return (
    <div>
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">Tenants</h2>
          <p className="mt-1 text-sm text-muted-foreground">Every workspace on the platform.</p>
        </div>
        <Link
          to="/platform/tenants/new"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium',
            'bg-primary text-primary-foreground shadow-md shadow-primary/20',
            'hover:opacity-90 transition-opacity',
          )}
        >
          <Plus className="h-4 w-4" />
          New tenant
        </Link>
      </header>

      {state.status === 'loaded' && total > 0 ? (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative min-w-56 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={search.q ?? ''}
              onChange={(e) => setSearch({ q: e.target.value || undefined })}
              placeholder="Search name, slug, domain or owner"
              aria-label="Search tenants"
              className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <FilterSelect
            label="Status"
            value={search.status ?? ''}
            options={STATUSES}
            onChange={(status) => setSearch({ status: status || undefined })}
          />
          <FilterSelect
            label="Tier"
            value={search.tier ?? ''}
            options={TIERS}
            onChange={(tier) => setSearch({ tier: tier || undefined })}
          />
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {filtering ? `${visible.length} of ${total}` : total}{' '}
              {total === 1 ? 'tenant' : 'tenants'}
            </span>
            <button
              type="button"
              onClick={exportCsv}
              disabled={visible.length === 0}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium',
                'hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50',
              )}
            >
              <Download className="h-3.5 w-3.5" />
              CSV
            </button>
          </div>
        </div>
      ) : null}

      {state.status === 'loading' ? <LoadingState /> : null}
      {state.status === 'error' ? <ErrorState message={state.message} /> : null}
      {state.status === 'loaded' && total === 0 ? <EmptyState /> : null}
      {state.status === 'loaded' && total > 0 && visible.length === 0 ? <NoMatches /> : null}
      {state.status === 'loaded' && visible.length > 0 ? (
        <TenantTable tenants={visible} sort={sort} dir={dir} onSort={toggleSort} />
      ) : null}
    </div>
  )
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: readonly string[]
  onChange: (value: string) => void
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      className="rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
    >
      <option value="">All {label.toLowerCase()}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option.replaceAll('_', ' ')}
        </option>
      ))}
    </select>
  )
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center gap-2 rounded-2xl border border-border bg-card/60 py-12 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading tenants…
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
      {message}
    </div>
  )
}

function NoMatches() {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card/60 py-12 text-center text-sm text-muted-foreground">
      No tenants match these filters.
    </div>
  )
}

function EmptyState() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-2xl border border-dashed border-border bg-card/60 p-10 text-center"
    >
      <div
        className="pointer-events-none absolute -top-16 left-1/2 h-40 w-40 -translate-x-1/2 rounded-full opacity-20"
        style={{
          background: 'radial-gradient(circle, var(--primary) 0%, transparent 70%)',
          filter: 'blur(36px)',
        }}
      />
      <div className="relative">
        <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15">
          <Database className="h-5 w-5" />
        </div>
        <h3 className="mt-4 text-lg font-semibold">No tenants yet</h3>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
          The platform has no tenants. Once someone signs up and gets a workspace provisioned,
          they'll appear here.
        </p>
      </div>
    </motion.div>
  )
}

function TenantTable({
  tenants,
  sort,
  dir,
  onSort,
}: {
  tenants: AdminTenant[]
  sort: SortKey
  dir: SortDir
  onSort: (key: SortKey) => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th sortKey="name" sort={sort} dir={dir} onSort={onSort}>
                Name
              </Th>
              <Th>Slug</Th>
              <Th sortKey="tier" sort={sort} dir={dir} onSort={onSort}>
                Tier
              </Th>
              <Th sortKey="status" sort={sort} dir={dir} onSort={onSort}>
                Status
              </Th>
              <Th>Owners</Th>
              <Th sortKey="members" sort={sort} dir={dir} onSort={onSort}>
                Members
              </Th>
              <Th sortKey="modules" sort={sort} dir={dir} onSort={onSort}>
                Modules
              </Th>
              <Th sortKey="createdAt" sort={sort} dir={dir} onSort={onSort}>
                Created
              </Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {tenants.map((tenant) => (
              <tr key={tenant.id} className="transition-colors hover:bg-accent/40">
                <Td>
                  <Link
                    to="/platform/tenants/$tenantId"
                    params={{ tenantId: tenant.id }}
                    className="group block"
                  >
                    <div className="font-medium text-foreground group-hover:text-primary transition-colors">
                      {tenant.name}
                    </div>
                    {tenant.emailDomain ? (
                      <div className="text-xs text-muted-foreground">{tenant.emailDomain}</div>
                    ) : null}
                  </Link>
                </Td>
                <Td>
                  <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{tenant.slug}</code>
                </Td>
                <Td>
                  <TierBadge tier={tenant.tier} />
                </Td>
                <Td>
                  <StatusBadge status={tenant.status} />
                </Td>
                <Td>
                  {tenant.members.length === 0 ? (
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : (
                    <div className="flex flex-col gap-0.5">
                      {tenant.members.map((m) => (
                        <span key={m.userId} className="text-xs">
                          <span className="font-medium">{m.email}</span>
                          <span className="text-muted-foreground"> · {m.role}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </Td>
                <Td>
                  <span className="text-xs tabular-nums">{tenant.members.length}</span>
                </Td>
                <Td>
                  <span className="text-xs tabular-nums">{tenant.moduleCount}</span>
                </Td>
                <Td>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(tenant.createdAt)}
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  )
}

function Th({
  children,
  sortKey,
  sort,
  dir,
  onSort,
}: {
  children: React.ReactNode
  sortKey?: SortKey
  sort?: SortKey
  dir?: SortDir
  onSort?: (key: SortKey) => void
}) {
  const active = sortKey !== undefined && sortKey === sort
  const Icon = active ? (dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown
  return (
    <th
      scope="col"
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
      className="px-4 py-2.5 text-left font-semibold"
    >
      {sortKey && onSort ? (
        <button
          type="button"
          onClick={() => onSort(sortKey)}
          className={cn(
            'inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-foreground',
            active && 'text-foreground',
          )}
        >
          {children}
          <Icon className={cn('h-3 w-3', !active && 'opacity-50')} />
        </button>
      ) : (
        children
      )}
    </th>
  )
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-4 py-3 align-top">{children}</td>
}

function TierBadge({ tier }: { tier: string }) {
  const label = tier === 'business' ? 'Business' : tier === 'enterprise' ? 'Enterprise' : 'Personal'
  const tone =
    tier === 'enterprise'
      ? 'bg-primary/10 text-primary ring-primary/25'
      : tier === 'business'
        ? 'bg-chart-3/10 text-chart-3 ring-chart-3/25'
        : 'bg-muted text-muted-foreground ring-border'
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-lg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
        tone,
      )}
    >
      {label}
    </span>
  )
}

function StatusBadge({ status }: { status: string | null }) {
  const label = (status ?? '—').replaceAll('_', ' ')
  const tone =
    status === 'active'
      ? 'bg-success/10 text-success ring-success/25'
      : status === 'paused' || status === 'pending_deletion'
        ? 'bg-warning/10 text-warning ring-warning/25'
        : status === 'suspended' || status === 'deleted'
          ? 'bg-destructive/10 text-destructive ring-destructive/25'
          : 'bg-muted text-muted-foreground ring-border'
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-lg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
        tone,
      )}
    >
      {label}
    </span>
  )
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}
